import { useEffect, useState } from 'react';
import { tradeApi } from './api.js';
import { StatusTag } from './RequestDetail.jsx';
import { typeLabels, bucketRequests } from './fields.js';

function ageOf(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days <= 0 ? 'today' : days === 1 ? '1 day' : `${days} days`;
}

/**
 * Investment Management's queue: every open request, in the buckets the team works from, oldest first.
 * Salesforce decides who may see it (a trading profile); anyone else gets its refusal.
 */
export default function ImQueue({ navigate }) {
  const [state, setState] = useState({ state: 'loading' });
  const [show, setShow] = useState('all'); // all | mine | unassigned

  function load() {
    setState(s => (s.state === 'ready' ? { ...s, refreshing: true } : { state: 'loading' }));
    tradeApi.imQueue().then(res => setState(res.ok ? { state: 'ready', ...res.body } : { state: 'failed', error: res.body.error, status: res.status }));
  }
  useEffect(() => { load(); }, []);

  if (state.state === 'loading') {
    return <div className="tt-loading"><div className="load-spinner" /><p>Reading the open requests from Salesforce…</p></div>;
  }
  if (state.state === 'failed') {
    return <div className="tt-page"><div className="tt-banner is-error" role="alert"><strong>The queue could not be read.</strong> {state.error}</div></div>;
  }

  const all = state.requests || [];
  const rows = show === 'mine' ? all.filter(r => r.assignedTraderId === state.userId)
    : show === 'unassigned' ? all.filter(r => !r.assignedTraderId) : all;
  const buckets = bucketRequests(rows).filter(b => b.rows.length);

  return (
    <div className="tt-page tt-wide">
      <header className="tt-page-head">
        <div>
          <p className="tt-kicker">Investment Management</p>
          <h1>Open requests <span className="tt-count">{all.length}</span></h1>
          <p className="tt-muted">Every open trade request, oldest first, in the queue it belongs to. Open one to see everything Salesforce holds around it and act on it.</p>
        </div>
        <div className="tt-toolbar">
          <div className="tt-chips" role="radiogroup" aria-label="Which requests">
            {[['all', 'All'], ['mine', 'Mine'], ['unassigned', 'Unassigned']].map(([k, label]) => (
              <label key={k} className={'tt-chip' + (show === k ? ' is-on' : '')}>
                <input type="radio" name="tt-im-show" checked={show === k} onChange={() => setShow(k)} />{label}
              </label>
            ))}
          </div>
          <button className="btn btn-light btn-sm" disabled={state.refreshing} onClick={load}>{state.refreshing ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </header>
      {buckets.length === 0 && <section className="tt-card"><p className="tt-muted">Nothing open.</p></section>}
      {buckets.map(b => (
        <section key={b.key} className={'tt-card' + (b.key === 'sentBack' ? ' is-quiet' : '')}>
          <h2>{b.title} <span className="tt-count">{b.rows.length}</span></h2>
          <table className="tt-table is-rows tt-im-table">
            <thead>
              <tr><th>Request</th><th>Household</th><th>Advisor</th><th>Accounts</th><th>Status</th><th>Waiting</th><th>Trader</th></tr>
            </thead>
            <tbody>
              {b.rows.map(r => (
                <tr key={r.id} onClick={() => navigate(`/trade/im/requests/${r.id}`)}>
                  <td>
                    <a href={`/trade/im/requests/${r.id}`} onClick={e => { e.preventDefault(); navigate(`/trade/im/requests/${r.id}`); }}>{r.name}</a>
                    <div className="tt-muted tt-small">{typeLabels(r.types)}{r.fromWizard ? ' · Move Money wizard' : r.source ? ` · ${r.source}` : ''}</div>
                  </td>
                  <td>{r.household}</td>
                  <td>{r.advisor}{r.createdBy && r.createdBy !== r.advisor ? <div className="tt-muted tt-small">sent by {r.createdBy}</div> : null}</td>
                  <td>{r.accountCount}{r.accounts && r.accounts.length ? <div className="tt-muted tt-small">{r.accounts.slice(0, 3).join(', ')}{r.accounts.length > 3 ? '…' : ''}</div> : null}</td>
                  <td><StatusTag status={r.status} /></td>
                  <td className="tt-muted">{ageOf(r.createdDate)}</td>
                  <td>{r.assignedTrader || <span className="tt-muted">–</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
